import { Link } from "react-router-dom";

import { openCookiePreferences } from "../CookieConsent";
import logo from "../../img/logo.svg";

/**
 * The redesigned footer: a dark rounded box that sits inside the marketing
 * page's boxed shell rather than spanning the viewport.
 *
 * Every link points at a route that exists — the design comp had columns for
 * Pricing, Reports, Changelog and Careers, and shipping those as 404s would be
 * worse than shipping fewer columns. Contact and the social links are carried
 * over from ContactFooter so nothing is lost by swapping this in.
 */
const COLUMNS = [
  {
    heading: "Product",
    links: [
      { label: "Check Your Water", to: "/app" },
      { label: "My Properties", to: "/app/my-properties" },
      { label: "Features", to: "/features" },
    ],
  },
  {
    heading: "Method",
    links: [
      { label: "How it works", to: "/features#how-it-works" },
      { label: "About Us", to: "/about" },
      { label: "Articles", to: "/articles" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      // Not a page: reopens the cookie banner over this one, choices filled in.
      { label: "Cookie preferences", onClick: openCookiePreferences },
    ],
  },
];

const LINK = "text-sm text-white/55 transition-colors hover:text-white";

const SOCIAL = [
  { label: "X", href: "https://twitter.com/zatoplan" },
  { label: "LinkedIn", href: "https://linkedin.com/company/zatoplan" },
  { label: "GitHub", href: "https://github.com/zatoplan" },
];

export default function MarketingFooter() {
  return (
    <footer className="rounded-xl bg-zp-night px-8 pb-9 mt-6 pt-11 sm:px-11">
      <div className="flex flex-col gap-10 lg:flex-row lg:justify-between">
        <div>
          <img src={logo} alt="Zatoplan" className="h-6 w-auto brightness-0 invert" />
          <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/50">
            Water quality, by service area
          </p>
          <a
            href="mailto:help@zatoplan.com"
            className="mt-5 inline-block text-sm text-white/70 underline-offset-4 transition-colors hover:text-white hover:underline"
          >
            help@zatoplan.com
          </a>
        </div>

        <div className="flex flex-wrap gap-x-14 gap-y-8">
          {COLUMNS.map(({ heading, links }) => (
            <div key={heading} className="min-w-[150px]">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-white">
                {heading}
              </h2>
              <ul className="mt-4 space-y-3">
                {links.map(({ label, to, onClick }) => (
                  <li key={label}>
                    {to ? (
                      <Link to={to} className={LINK}>
                        {label}
                      </Link>
                    ) : (
                      <button type="button" onClick={onClick} className={LINK}>
                        {label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-11 flex flex-col gap-4 border-t border-white/15 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          © {new Date().getFullYear()} Zatoplan
        </p>
        <div className="flex gap-5">
          {SOCIAL.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45 transition-colors hover:text-white"
            >
              {label}
            </a>
          ))}
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          Not a substitute for a certified lab test
        </p>
      </div>
    </footer>
  );
}
